import { User } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { normalizeEmail, toDate } from '../lib/format';
import { AppUser } from '../types';

const USERS = 'users';

const fromData = (id: string, data: any): AppUser => ({
  id,
  uid: data.uid || id,
  email: data.email || '',
  displayName: data.displayName || data.email || '',
  photoURL: data.photoURL || null,
  isAdmin: data.isAdmin === true,
  createdAt: toDate(data.createdAt),
  lastLoginAt: toDate(data.lastLoginAt),
});

export const userService = {
  /** Reads the caller's own user document (always permitted by the rules). */
  async getUser(uid: string): Promise<AppUser | null> {
    const snap = await getDoc(doc(db, USERS, uid));
    return snap.exists() ? fromData(snap.id, snap.data()) : null;
  },

  /**
   * Creates the profile on first sign-in or refreshes it afterwards.
   * `isAdmin` is never written from the client; admins are promoted by another admin.
   */
  async upsertProfile(user: User, existing: AppUser | null): Promise<AppUser> {
    const ref = doc(db, USERS, user.uid);
    const displayName = user.displayName || existing?.displayName || user.email || '';
    const photoURL = user.photoURL || null;

    if (existing) {
      await updateDoc(ref, { lastLoginAt: serverTimestamp(), displayName, photoURL });
      return { ...existing, displayName, photoURL, lastLoginAt: new Date() };
    }

    const email = normalizeEmail(user.email || '');
    await setDoc(ref, {
      uid: user.uid,
      email,
      displayName,
      photoURL,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    });
    return {
      id: user.uid,
      uid: user.uid,
      email,
      displayName,
      photoURL,
      isAdmin: false,
      createdAt: new Date(),
      lastLoginAt: new Date(),
    };
  },

  async getAllUsers(): Promise<AppUser[]> {
    const snapshot = await getDocs(collection(db, USERS));
    return snapshot.docs
      .map((d) => fromData(d.id, d.data()))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  },

  async setAdminStatus(uid: string, isAdmin: boolean): Promise<void> {
    await updateDoc(doc(db, USERS, uid), { isAdmin });
  },
};
